import type { SolarProposalImpactDto } from "~/api/dto/solar-proposal-impact.dto"

import type { SolarImpact, SolarImpactStat } from "./solar-presentation"
import { formatNumber } from "./solar-proposal-design"
import { formatUsd } from "../BatteryOnlyProposal/view-helpers"

/** Where each figure's formula comes from, as V1 links them. */
const SOURCES = {
  co2: { name: `NREL`, href: `https://www.nrel.gov/docs/fy07osti/41998.pdf` },
  trees: {
    name: `EPA`,
    href: `https://www.epa.gov/energy/greenhouse-gas-equivalencies-calculator`,
  },
  homeValue: {
    name: `Berkeley Lab`,
    href: `https://emp.lbl.gov/news/berkeley-lab-illuminates`,
  },
} as const

/**
 * One card per figure the impact read computed, worded to claim only what it
 * computes. Formatting only; the gateway does the math and the rounding.
 */
export const toSolarImpact = ({
  co2MetricTons25yr,
  treesEquivalent,
  homeValueAdded,
}: SolarProposalImpactDto): SolarImpact => {
  const stats: SolarImpactStat[] = []

  if (co2MetricTons25yr !== null) {
    stats.push({
      icon: `cloud`,
      value: formatNumber(co2MetricTons25yr, 0),
      unit: `metric tons`,
      label: `of CO₂ offset over 25 years`,
      detail: `Based on your system's projected 25-year production.`,
      source: SOURCES.co2,
    })
  }

  if (treesEquivalent !== null) {
    stats.push({
      icon: `leaf`,
      value: formatNumber(treesEquivalent, 0),
      unit: `trees`,
      label: `planted, equivalent`,
      detail: `The 25-year CO₂ offset, expressed as trees planted.`,
      source: SOURCES.trees,
    })
  }

  if (homeValueAdded !== null) {
    stats.push({
      icon: `home`,
      value: formatUsd(homeValueAdded),
      unit: null,
      label: `estimated value added to your home`,
      detail: `Based on Berkeley Lab research on solar home sales.`,
      source: SOURCES.homeValue,
    })
  }

  // TPO never carries home value, so this drops the claim for TPO too.
  return { claimsHomeValue: homeValueAdded !== null, stats }
}
